/**
 * Zod-Schemas für Stundenzettel-Erfassung + Freigabe-Statusmaschine.
 */
import { z } from 'zod';
import { STAFF_TIMESHEET_REJECT_CATEGORIES, TIMESHEET_REJECT_CATEGORIES } from '../enums/index.js';
import type { TimesheetStatus } from '../enums/index.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format YYYY-MM-DD');

const timesheetBaseFields = z.object({
  assignmentId: z.string().uuid().optional(),
  periodStart: isoDate,
  periodEnd: isoDate,
  totalHours: z.number().min(0).max(744).optional(),
  notes: z.string().trim().max(2000).optional(),
});

const periodOrdered = (v: { periodStart: string; periodEnd: string }) =>
  v.periodEnd >= v.periodStart;
const periodError = {
  message: 'Ende darf nicht vor dem Start liegen',
  path: ['periodEnd'],
};

export const createTimesheetSchema = timesheetBaseFields.refine(periodOrdered, periodError);
export type CreateTimesheetInput = z.infer<typeof createTimesheetSchema>;

export const timesheetEntryItemSchema = z.object({
  entryType: z.enum(['work', 'break']).default('work'),
  startAt: z.string(),
  endAt: z.string(),
  notes: z.string().trim().max(500).optional(),
});

export const setTimesheetEntriesSchema = z.object({
  timesheetId: z.string().uuid(),
  entries: z.array(timesheetEntryItemSchema),
});
export type SetTimesheetEntriesInput = z.infer<typeof setTimesheetEntriesSchema>;

/** Begründung ist bei Ablehnung/Override Pflicht — nie stilles Wegklicken. */
const mandatoryReason = z
  .string()
  .trim()
  .min(10, 'Begründung erforderlich (mindestens 10 Zeichen)')
  .max(2000);

/** Entleiher-Review: bestätigen oder ablehnen (Ablehnung nur mit Kategorie + Begründung). */
export const clientReviewTimesheetSchema = z.discriminatedUnion('action', [
  z.object({
    timesheetId: z.string().uuid(),
    action: z.literal('approve'),
    notes: z.string().trim().max(2000).optional(),
  }),
  z.object({
    timesheetId: z.string().uuid(),
    action: z.literal('reject'),
    category: z.enum(TIMESHEET_REJECT_CATEGORIES),
    notes: mandatoryReason,
  }),
]);
export type ClientReviewTimesheetInput = z.infer<typeof clientReviewTimesheetSchema>;

/** Entleiher korrigiert Zeiten direkt — Ursprungswerte bleiben erhalten. */
export const clientCorrectTimesheetSchema = z.object({
  timesheetId: z.string().uuid(),
  correctedTotalHours: z.number().min(0).max(744),
  notes: z.string().trim().max(2000).optional(),
});
export type ClientCorrectTimesheetInput = z.infer<typeof clientCorrectTimesheetSchema>;

/** Worker-Antwort auf eine Kunden-Korrektur: Ein-Klick bestätigen oder widersprechen. */
export const workerCorrectionResponseSchema = z.discriminatedUnion('action', [
  z.object({ timesheetId: z.string().uuid(), action: z.literal('confirm') }),
  z.object({
    timesheetId: z.string().uuid(),
    action: z.literal('dispute'),
    notes: mandatoryReason,
  }),
]);
export type WorkerCorrectionResponseInput = z.infer<typeof workerCorrectionResponseSchema>;

/** Staff-Review (finale Freigabe) — Ablehnung nur mit Kategorie + Begründung. */
export const staffReviewTimesheetSchema = z.discriminatedUnion('action', [
  z.object({
    timesheetId: z.string().uuid(),
    action: z.literal('approve'),
    notes: z.string().trim().max(2000).optional(),
  }),
  z.object({
    timesheetId: z.string().uuid(),
    action: z.literal('reject'),
    category: z.enum(STAFF_TIMESHEET_REJECT_CATEGORIES),
    notes: mandatoryReason,
  }),
]);
export type StaffReviewTimesheetInput = z.infer<typeof staffReviewTimesheetSchema>;

/** Staff-Override: Freigabe ohne Kundenbestätigung — Pflicht-Begründung, eigene Audit-Action. */
export const overrideApproveTimesheetSchema = z.object({
  timesheetId: z.string().uuid(),
  reason: mandatoryReason,
});
export type OverrideApproveTimesheetInput = z.infer<typeof overrideApproveTimesheetSchema>;

/** Worker reicht abgelehnten Zettel korrigiert neu ein. */
export const resubmitTimesheetSchema = z.object({
  timesheetId: z.string().uuid(),
  totalHours: z.number().min(0).max(744).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ResubmitTimesheetInput = z.infer<typeof resubmitTimesheetSchema>;

/** Staff-Erfassung "im Namen" — Herkunft Pflicht, Beleg optional. */
export const staffCreateTimesheetSchema = timesheetBaseFields
  .extend({
    workerId: z.string().uuid(),
    source: z.enum(['client_system', 'staff_entry']),
    evidenceDocumentId: z.string().uuid().optional(),
  })
  .refine(periodOrdered, periodError);
export type StaffCreateTimesheetInput = z.infer<typeof staffCreateTimesheetSchema>;

/**
 * Statusmaschine — Single Source of Truth für erlaubte Übergänge.
 * Die Repos erzwingen sie ATOMAR (`WHERE status IN allowedFrom`); die Router
 * nutzen sie für sprechende Fehlermeldungen; die UI blendet Aktionen ein/aus.
 *
 * Sonderfall: Zettel OHNE Einsatz (kein Kunde) darf Staff direkt aus
 * `submitted` freigeben — abgebildet über `STAFF_APPROVE_FROM_NO_CLIENT`.
 */
export const TIMESHEET_TRANSITIONS = {
  submit: { from: ['draft'] },
  clientApprove: { from: ['submitted'] },
  clientCorrect: { from: ['submitted'] },
  clientReject: { from: ['submitted'] },
  confirmCorrection: { from: ['client_corrected'] },
  disputeCorrection: { from: ['client_corrected'] },
  staffApprove: { from: ['client_approved'] },
  staffReject: { from: ['submitted', 'client_approved', 'client_corrected', 'disputed'] },
  overrideApprove: { from: ['submitted', 'client_corrected', 'disputed', 'rejected'] },
  resubmit: { from: ['rejected'] },
} as const satisfies Record<string, { from: readonly TimesheetStatus[] }>;

/** Staff-Direktfreigabe für Zettel ohne Kunden (assignmentId null). */
export const STAFF_APPROVE_FROM_NO_CLIENT: readonly TimesheetStatus[] = [
  'submitted',
  'client_approved',
];
